import { useForm, FormProvider } from "react-hook-form";
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import SmallSpinner from "@/components/shared/spinners/small-spinner";
import Textarea from "@/components/forms/textarea";
import Text from "@/components/forms/text";
import Select from "@/components/forms/select";
import Files, { type FilesValue } from "@/components/forms/files";
import RequiredFieldsLegend from "@/components/forms/required-fields-legend";
import { usePostContact } from '@/hooks/api/use-contact';
import { useContracts } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { pushEvent } from '@/lib/analytics';
import type { Contract } from '@/types/api/contract';
import type { ContactFormState, GetOptionLabel } from '@/pages/contact/contactez-nous';

interface FormulaireContactValues {
    contract_id: string | number;
    police: string;
    message: string;
    asap: boolean;
    nom?: string;
    prenom?: string;
    email?: string;
    telephone: string;
    files: FilesValue;
    motif_contact: string;
}

export interface FormulaireContactProps {
    formState?: ContactFormState;
    getOptionLabel: GetOptionLabel;
}

interface FormulaireContactContentProps extends FormulaireContactProps {
    contracts: Contract[];
}

const FormulaireContactContent = (props: FormulaireContactContentProps) => {
    const {getOptionLabel, contracts} = props;
    const formState = props.formState || {};
    const user = useCurrentUser();
    const postContact = usePostContact();
    const loading = postContact.isPending;
    const hasSeveralContracts = contracts?.length > 1;

    const initialValues: FormulaireContactValues = {
        contract_id: contracts && contracts.length === 1 ? contracts[0]!.id : '',
        police: contracts && contracts.length === 1 ? (contracts[0]!.numeroPolice as string) : '',
        message: '',
        asap: true,
        nom: user.lastname,
        prenom: user.firstname,
        email: user.email,
        telephone: '',
        files: [] as unknown as FilesValue,
        motif_contact: props.formState?.motif_contact || '',
    };

    const methods = useForm<FormulaireContactValues>({
        defaultValues: initialValues,
        mode: 'onSubmit',
    });

    const { handleSubmit, formState: { errors }, watch, setValue, register } = methods;
    const values = watch();

    const getFlowPath = () => {
        const path = [];

        if (formState?.motif_contact) {
            const label = getOptionLabel('motif_contact', formState.motif_contact);
            if (label) {
                path.push(label);
            }
        }

        // Ajouter les étapes suivantes selon le parcours
        if (formState?.proc_devis_deja === 'envoyer_message' || formState?.etape_3_proc_devis_deja === 'envoyer_message') {
            path.push('Envoyer un message');
        }

        return path.join(' > ');
    };

    const selectContract = (numeroPolice: string) => {
        const contract = contracts.find(c => String(c.numeroPolice) === String(numeroPolice));

        setValue('police', numeroPolice, { shouldValidate: true });
        setValue('contract_id', contract?.id || '');
    };

    const handleFormSubmit = (values: FormulaireContactValues) => {
        // Soumission avec callback de succès pour le tracking
        postContact.mutate({
            form: values,
            successFunction: () => {
                const clientType = 'Déjà client';
                const reason = getOptionLabel('motif_contact', formState?.motif_contact);
                const flow = getFlowPath();

                pushEvent('contactFormValid', {
                    'contactFormValid': {
                        'reason': reason,
                        'clientType': clientType,
                        'flow': flow
                    }
                })

                methods.reset();
            }
        });
    };

    return (
        <div>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                    <p className="font-semibold text-muted-foreground mt-6">Formulaire de contact</p>
                    <RequiredFieldsLegend className="mt-2" />

                    <div className="space-y-4 mt-6">
                        {hasSeveralContracts ? (
                            <Select
                                {...register('police', { required: 'Veuillez sélectionner le champ « Numéro de contrat »' })}
                                label='Numéro de contrat'
                                value={values.police}
                                error={errors.police?.message}
                                onChange={e => selectContract(e.target.value)}
                                required
                            >
                                <option value="">Sélectionnez votre contrat</option>
                                {contracts.map(contract => (
                                    <option key={contract.id} value={contract.numeroPolice}>
                                        Contrat n°{contract.numeroPolice}{contract.vehicule?.marque ? ` - ${contract.vehicule.marque}` : ''}
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <Text
                                {...register('police', { required: 'Veuillez renseigner le champ « Numéro de contrat »' })}
                                label='Numéro de contrat'
                                value={values.police}
                                error={errors.police?.message}
                                required
                            />
                        )}
                        <Text
                            {...register('nom', { required: 'Veuillez renseigner le champ « Nom »' })}
                            autoComplete="family-name"
                            label='Nom'
                            value={values.nom}
                            error={errors.nom?.message}
                            required
                        />
                        <Text
                            {...register('prenom', { required: 'Veuillez renseigner le champ « Prénom »' })}
                            autoComplete="given-name"
                            label='Prénom'
                            value={values.prenom}
                            error={errors.prenom?.message}
                            required
                        />
                        <Text
                            type="email"
                            {...register('email', {
                                required: 'Veuillez renseigner le champ « Email »',
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Adresse email invalide. Exemple : prenom.nom@domaine.fr',
                                },
                            })}
                            autoComplete="email"
                            label='Email'
                            value={values.email}
                            error={errors.email?.message}
                            aria-describedby="email-help"
                            required
                        />
                        <p id="email-help" className="text-xs text-muted-foreground -mt-3">
                            Exemple : prenom.nom@domaine.fr
                        </p>
                        <Text
                            type="tel"
                            {...register('telephone', {
                                required: 'Veuillez renseigner le champ « Téléphone »',
                                pattern: {
                                    value: /^(?:(?:\+|00)33[\s.-]?(?:\(0\)[\s.-]?)?|0)[1-9](?:[\s.-]?\d{2}){4}$/,
                                    message: 'Numéro de téléphone invalide. Exemple : 06 12 34 56 78',
                                },
                            })}
                            autoComplete="tel"
                            label='Téléphone'
                            value={values.telephone}
                            error={errors.telephone?.message}
                            aria-describedby="tel-help"
                            required
                        />
                        <p id="tel-help" className="text-xs text-muted-foreground -mt-3">
                            Exemple : 06 12 34 56 78
                        </p>
                        <Textarea
                            required
                            label={'Descriptif de votre demande / champ libre'}
                            {...register('message', { required: 'Veuillez renseigner le champ « Descriptif de votre demande »' })}
                            error={errors.message?.message}
                            value={values.message}
                            maxLength={400}
                        />
                        <Files
                            label={'Envoyer un fichier'}
                            name={`files[X]`}
                            arrayName={'files'}
                            value={values.files}
                            setValue={setValue}
                            code={'X'}
                            accept={'image/*,application/pdf'}
                        />
                        <p className="text-muted small mt-1">Format des fichiers autorisés : PDF, JPG, PNG</p>
                    </div>

                    <div className="mt-6">
                        {loading ? <SmallSpinner /> : (
                            <Button type="submit" className="w-full cursor-pointer">
                                <Send className="w-4 h-4" />
                                Envoyer
                            </Button>
                        )}
                    </div>
                </form>
            </FormProvider>
        </div>
    );
};

/** Formulaire de contact, affiché une fois les contrats chargés. */
const FormulaireContact = (props: FormulaireContactProps) => {
    const contractsQuery = useContracts();

    if (contractsQuery.isPending) return <SmallSpinner />;

    return <FormulaireContactContent {...props} contracts={contractsQuery.data?.data ?? []} />;
};

export default FormulaireContact;
